import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { LoadingState, PageContainer, PageHeader } from '@/components/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateUserMe } from '@/hooks/mutations/use-update-user-me';
import { useUserMe } from '@/hooks/queries/use-user-me';

export const Route = createFileRoute('/(app)/account/edit')({
  component: EditProfilePage,
});

const formSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
});

type FormValues = z.infer<typeof formSchema>;

function EditProfilePage() {
  const navigate = useNavigate();
  const { data, isLoading } = useUserMe();
  const updateMutation = useUpdateUserMe();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      name: data?.user?.name ?? '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await updateMutation.mutateAsync(values);
      toast.success('Perfil atualizado com sucesso!');
      navigate({ to: '/account' });
    } catch {
      toast.error('Erro ao atualizar perfil. Tente novamente.');
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState message="Carregando..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Editar Perfil"
          actions={
            <Link
              to="/account"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Link>
          }
        />

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  placeholder="Seu nome"
                  disabled={updateMutation.isPending}
                  {...register('name')}
                />
                <FormError message={errors.name?.message} />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: '/account' })}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
